import 'bootstrap/dist/css/bootstrap.css';
import { useState, useEffect } from 'react';
import Spinner from '../../shared/spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCogs, faSave, faHandHoldingUsd, 
    faExclamationTriangle, faStar, 
    faBuilding, faFileInvoiceDollar, faUniversity, faImage, faTrash, faPlus, faCheckCircle, faEdit, faEnvelope,
    faCalculator // Adicionei ícone de calculadora para a nova seção
} from '@fortawesome/free-solid-svg-icons';
import CustomAlert from '../../shared/customAlert';
import ComissaoService, { ParametrosCnabDTO } from '../../services/comissaoService';
import { ComissaoParametrosGeraisDTO } from '../../shared/dto/comissaoParametrosGeraisDTO';
import { format } from 'date-fns';

// Mock das Empresas de Fidelidade
const EMPRESAS_FIDELIDADE = [
    { id: 1, nome: 'Livelo' },
    { id: 2, nome: 'Latam Pass' },
    { id: 3, nome: 'TudoAzul' },
    { id: 4, nome: 'Dotz' },
    { id: 5, nome: 'Smiles' }
];

// Interface estendida
interface ContaBancariaExtended extends ParametrosCnabDTO {
    tempId?: number; 
    principal: boolean;
    nomeIdentificacao: string; 
}

function ComissaoParametrosGeraisComponent() {
  
  // --- ESTADOS ---
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Estado dos Parâmetros Gerais
  const [formParams, setFormParams] = useState<ComissaoParametrosGeraisDTO & { 
      empresaFidelidadeId?: number, 
      pontosPorReal?: number, 
      maximoParcelasNegociacao?: number,
      logomarcaBase64?: string,
      periodicidadePagamentoPremiacao?: string,
      // NOVOS CAMPOS DE NOTIFICAÇÃO
      horaDisparoEmail?: string,
      emailNotificacaoDestino?: string,
      smtpHost?: string,
      smtpPort?: number,
      smtpUsername?: string,
      smtpPassword?: string,
      smtpAuth?: boolean,
      smtpStarttls?: boolean,
      // NOVOS CAMPOS DE REGRAS
      tipoComissao?: string,
      descontarNegociacoes?: boolean,
      descontarEstornos?: boolean
  }>({
    limiteEndividamento: 0,
    usaPontos: false,
    periodicidadeEmissaoNFSe: 'MENSAL', 
    periodicidadePagamentoPremiacao: 'MENSAL',
    periodicidadePagamentoPontos: '',
    percentualMulta30Dias: 0,
    percentualMulta60Dias: 0,
    percentualMulta90Dias: 0,
    empresaFidelidadeId: 0,
    pontosPorReal: 0,
    maximoParcelasNegociacao: 6,
    logomarcaBase64: '',
    // Inicialização dos campos de email
    horaDisparoEmail: '',
    emailNotificacaoDestino: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpAuth: true,
    smtpStarttls: true,
    // Inicialização dos novos campos de regras
    tipoComissao: 'PADRAO',
    descontarNegociacoes: false,
    descontarEstornos: false
  });

  // --- ESTADOS DE CONTAS BANCÁRIAS ---
  const [listaContas, setListaContas] = useState<ContaBancariaExtended[]>([]);
  
  const [contaForm, setContaForm] = useState<ContaBancariaExtended>({
      empresaNome: '', empresaCnpj: '', empresaLogradouro: '', empresaNumero: '', empresaCidade: '', empresaCep: '', empresaUf: '',
      bancoCodigo: '001', bancoNome: 'BANCO DO BRASIL', agencia: '', agenciaDv: '', conta: '', contaDv: '', convenio: '',
      principal: false,
      nomeIdentificacao: ''
  });
  
  const [editandoIndex, setEditandoIndex] = useState<number | null>(null);

  const comissaoService = new ComissaoService();

  // --- HELPERS ---
  const formatCurrency = (value: number) => {
      if (value === undefined || value === null) return 'R$ 0,00';
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
      let value = e.target.value;
      value = value.replace(/\D/g, "");
      const floatValue = parseFloat(value) / 100;
      setFormParams(prev => ({ ...prev, [field]: floatValue }));
  };

  // --- CARREGAMENTO ---
  useEffect(() => {
    carregarDadosCompletos();
  }, []); 

  const carregarDadosCompletos = async () => {
    setLoading(true);
    try {
        try {
            const respGerais = await comissaoService.buscarParametrosGerais();
            if (respGerais.data) setFormParams(prev => ({ ...prev, ...respGerais.data }));
        } catch (err: any) {
             console.error("Erro ao carregar gerais", err);
        }

        try {
            const respCnab = await comissaoService.buscarConfigCnab(); 
            if (Array.isArray(respCnab.data)) {
                setListaContas(respCnab.data);
            } else if (respCnab.data) {
                setListaContas([{ ...respCnab.data, principal: true, nomeIdentificacao: 'Conta Principal', tempId: Date.now() }]);
            }
        } catch (err: any) {
             console.error("Erro ao carregar CNAB", err);
        }
    } catch (err) {
        setAlert({ message: 'Erro ao carregar configurações do sistema.', type: 'error' });
    } finally {
        setLoading(false);
    }
  };

  // --- LÓGICA DE CONTAS BANCÁRIAS ---
  const limparFormularioConta = () => {
      setContaForm({
          empresaNome: '', empresaCnpj: '', empresaLogradouro: '', empresaNumero: '', empresaCidade: '', empresaCep: '', empresaUf: '',
          bancoCodigo: '', bancoNome: '', agencia: '', agenciaDv: '', conta: '', contaDv: '', convenio: '',
          principal: false, nomeIdentificacao: ''
      });
      setEditandoIndex(null);
  };

  const salvarContaNaLista = () => {
      if (!contaForm.nomeIdentificacao || !contaForm.bancoCodigo || !contaForm.conta || !contaForm.agencia) {
          setAlert({ message: 'Preencha o Nome da Conta, Banco, Agência e Conta.', type: 'warning' });
          return;
      }
      let novaLista = [...listaContas];
      let contaParaSalvar = { ...contaForm };
      
      if (novaLista.length === 0) contaParaSalvar.principal = true;
      if (contaParaSalvar.principal) novaLista = novaLista.map(c => ({ ...c, principal: false }));

      if (editandoIndex !== null) novaLista[editandoIndex] = contaParaSalvar;
      else {
          contaParaSalvar.tempId = Date.now();
          novaLista.push(contaParaSalvar);
      }
      setListaContas(novaLista);
      limparFormularioConta();
  };

  const editarConta = (index: number) => {
      setContaForm({ ...listaContas[index] });
      setEditandoIndex(index);
  };

  const removerConta = (index: number) => {
      const contaRemovida = listaContas[index];
      const novaLista = listaContas.filter((_, i) => i !== index);
      if (contaRemovida.principal && novaLista.length > 0) novaLista[0].principal = true;
      setListaContas(novaLista);
      if (editandoIndex === index) limparFormularioConta();
  };

  const definirComoPrincipal = (index: number) => {
      const novaLista = listaContas.map((c, i) => ({ ...c, principal: i === index }));
      setListaContas(novaLista);
  };

  // --- HANDLER DE IMAGEM ---
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
          if (file.size > 200 * 1024) {
              setAlert({ message: 'Imagem muito grande. Máx: 200KB.', type: 'warning' });
              return;
          }
          const reader = new FileReader();
          reader.onloadend = () => {
              setFormParams(prev => ({ ...prev, logomarcaBase64: reader.result as string }));
          };
          reader.readAsDataURL(file);
      }
  };

  const removerLogo = () => setFormParams(prev => ({ ...prev, logomarcaBase64: '' }));

  // --- AÇÃO DE SALVAR ---
  const salvarTudo = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formParams.usaPontos && (!formParams.empresaFidelidadeId || !formParams.pontosPorReal)) {
        return setAlert({ message: 'Verifique as configurações de pontos.', type: 'warning' });
    }
    if (listaContas.length === 0) {
         return setAlert({ message: 'Adicione pelo menos uma conta bancária.', type: 'warning' });
    }

    try {
      setLoading(true);
      const respGerais = await comissaoService.salvarParametrosGerais(formParams);
      setFormParams(prev => ({ ...prev, ...respGerais.data }));
      await comissaoService.salvarConfigCnab(listaContas as any); 
      setAlert({ message: 'Configurações salvas com sucesso!', type: 'success' });
    } catch (err: any) {
      setAlert({ message: 'Erro ao salvar. Verifique os dados.', type: 'error' });
    } finally {
        setLoading(false);
    }
  };

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
      
      <div className="position-relative m-md-3">
        {loading ? <Spinner loading={loading} /> : (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-body">
              <div className="card-header bg-light">
                <h5><FontAwesomeIcon icon={faCogs} /> Configurações Gerais do Sistema</h5>
              </div>
              
              <form onSubmit={salvarTudo} style={{ marginTop: '20px' }}>
                
                {/* 1. IDENTIDADE VISUAL */}
                <h6 className="text-muted border-bottom pb-2 mb-3">
                    <FontAwesomeIcon icon={faImage} className="me-2"/> Identidade Visual (Fatura e Relatórios)
                </h6>
                <div className="row mb-4 align-items-center">
                    <div className="col-md-6">
                        <label className="form-label">Logomarca</label>
                        <input type="file" className="form-control" accept="image/*" onChange={handleImageUpload} />
                    </div>
                    <div className="col-md-6 d-flex justify-content-center">
                        {formParams.logomarcaBase64 ? (
                            <div className="text-center p-2 border rounded bg-light position-relative" style={{ minWidth: '200px' }}>
                                <img src={formParams.logomarcaBase64} alt="Logo" style={{ maxHeight: '60px', maxWidth: '100%' }} />
                                <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1" onClick={removerLogo}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        ) : (
                            <div className="text-center p-3 border rounded border-dashed text-muted bg-light w-100">
                                <small>Nenhuma logo carregada</small>
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. LIMITES E CICLOS */}
                <h6 className="text-muted border-bottom pb-2 mb-3">
                    <FontAwesomeIcon icon={faHandHoldingUsd} className="me-2"/> Limites Financeiros & Ciclos de Faturamento
                </h6>
                <div className="row mb-4">
                    <div className="col-md-3 form-group">
                        <label>Limite Endividamento (R$)</label>
                        <input 
                            type="text" 
                            className="form-control"
                            value={formatCurrency(formParams.limiteEndividamento)}
                            onChange={(e) => handleCurrencyChange(e, 'limiteEndividamento')}
                        />
                    </div>
                    
                    <div className="col-md-3 form-group">
                        <label>Max. Parcelas (Negoc.)</label>
                        <input 
                            type="number" className="form-control"
                            value={formParams.maximoParcelasNegociacao || ''}
                            onChange={e => setFormParams({...formParams, maximoParcelasNegociacao: parseInt(e.target.value)})}
                        />
                    </div>

                    <div className="col-md-3 form-group">
                        <label className="fw-bold text-primary">Ciclo Fatura Padrão</label>
                        <select 
                            className="form-select"
                            value={formParams.periodicidadeEmissaoNFSe}
                            onChange={e => setFormParams({...formParams, periodicidadeEmissaoNFSe: e.target.value})}
                        >
                            <option value="DIARIO">Diário</option>
                            <option value="SEMANAL">Semanal</option>
                            <option value="QUINZENAL">Quinzenal</option>
                            <option value="MENSAL">Mensal</option>
                        </select>
                    </div>

                    <div className="col-md-3 form-group">
                        <label className="fw-bold text-success">Ciclo Fatura Diferida</label>
                        <select 
                            className="form-select"
                            value={formParams.periodicidadePagamentoPremiacao || 'MENSAL'}
                            onChange={e => setFormParams({...formParams, periodicidadePagamentoPremiacao: e.target.value})}
                        >
                            <option value="DIARIO">Diário</option>
                            <option value="SEMANAL">Semanal</option>
                            <option value="QUINZENAL">Quinzenal</option>
                            <option value="MENSAL">Mensal</option>
                        </select>
                    </div>
                </div>

                {/* 2.1 NOVO: REGRAS DE CÁLCULO E DESCONTOS */}
                <h6 className="text-muted border-bottom pb-2 mb-3 mt-4">
                    <FontAwesomeIcon icon={faCalculator} className="me-2"/> Regras de Cálculo e Descontos
                </h6>
                <div className="card p-3 bg-light mb-4 border-0">
                    <div className="row">
                        <div className="col-md-4 form-group">
                            <label className="fw-bold">Tipo de Comissão</label>
                            <select 
                                className="form-select"
                                value={formParams.tipoComissao || 'PADRAO'}
                                onChange={e => setFormParams({...formParams, tipoComissao: e.target.value})}
                            >
                                <option value="PADRAO">Comissão Padrão</option>
                                <option value="PREMIACAO">Comissão Diferida</option>
                            </select>
                        </div>
                        <div className="col-md-4 d-flex align-items-center pt-3">
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" type="checkbox" id="checkNegociacoes"
                                    style={{ cursor: 'pointer', transform: 'scale(1.2)', marginRight: '10px' }}
                                    checked={formParams.descontarNegociacoes || false}
                                    onChange={e => setFormParams({...formParams, descontarNegociacoes: e.target.checked})}
                                />
                                <label className="form-check-label fw-bold" htmlFor="checkNegociacoes">Descontar Negociações</label>
                            </div>
                        </div>
                        <div className="col-md-4 d-flex align-items-center pt-3">
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" type="checkbox" id="checkEstornos"
                                    style={{ cursor: 'pointer', transform: 'scale(1.2)', marginRight: '10px' }}
                                    checked={formParams.descontarEstornos || false}
                                    onChange={e => setFormParams({...formParams, descontarEstornos: e.target.checked})}
                                />
                                <label className="form-check-label fw-bold" htmlFor="checkEstornos">Descontar Estornos</label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. PROGRAMA DE PONTOS */}
                <h6 className="text-muted border-bottom pb-2 mb-3">
                    <FontAwesomeIcon icon={faStar} className="me-2"/> Programa de Pontos
                </h6>
                <div className={`p-3 border rounded mb-4 ${formParams.usaPontos ? 'bg-white border-primary' : 'bg-light'}`}>
                    <div className="form-check form-switch mb-3">
                        <input 
                            className="form-check-input" type="checkbox" id="usaPontos"
                            style={{ cursor: 'pointer', transform: 'scale(1.2)', marginRight: '10px' }}
                            checked={formParams.usaPontos}
                            onChange={e => setFormParams({...formParams, usaPontos: e.target.checked})}
                        />
                        <label className="form-check-label fw-bold" htmlFor="usaPontos">Ativar Integração</label>
                    </div>
                    {formParams.usaPontos && (
                        <div className="row">
                            <div className="col-md-4 form-group">
                                <label>Empresa de Fidelidade</label>
                                <select 
                                    className="form-control"
                                    value={formParams.empresaFidelidadeId || ''}
                                    onChange={e => setFormParams({...formParams, empresaFidelidadeId: parseInt(e.target.value)})}
                                >
                                    <option value="">Selecione...</option>
                                    {EMPRESAS_FIDELIDADE.map(emp => <option key={emp.id} value={emp.id}>{emp.nome}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4 form-group">
                                <label>Taxa (Pontos por R$ 1,00)</label>
                                <input 
                                    type="number" className="form-control"
                                    value={formParams.pontosPorReal || ''}
                                    onChange={e => setFormParams({...formParams, pontosPorReal: parseFloat(e.target.value)})}
                                />
                            </div>
                            <div className="col-md-4 form-group">
                                <label>Pagamento dos Pontos</label>
                                <select 
                                    className="form-control"
                                    value={formParams.periodicidadePagamentoPontos || ''}
                                    onChange={e => setFormParams({...formParams, periodicidadePagamentoPontos: e.target.value})}
                                >
                                    <option value="">Selecione...</option>
                                    <option value="MENSAL">Mensal</option>
                                    <option value="ANUAL">Anual</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* --- NOVA SEÇÃO: CONFIGURAÇÃO DE NOTIFICAÇÃO E SMTP --- */}
                <h6 className="text-muted border-bottom pb-2 mb-3 mt-4">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-warning"/> Notificações e E-mail (SMTP)
                </h6>
                <div className="card mb-4 border-light shadow-sm bg-white">
                    <div className="card-body">
                        {/* Linha 1: Configuração do Disparo */}
                        <div className="row mb-3">
                            <div className="col-md-3 form-group">
                                <label className="fw-bold">Hora do Disparo</label>
                                <input 
                                    type="time" className="form-control"
                                    value={formParams.horaDisparoEmail || ''}
                                    onChange={e => setFormParams({...formParams, horaDisparoEmail: e.target.value})}
                                />
                                <small className="text-muted">Horário diário de verificação</small>
                            </div>
                            <div className="col-md-9 form-group">
                                <label className="fw-bold">E-mail(s) de Destino</label>
                                <input 
                                    type="text" className="form-control" placeholder="ex: financeiro@empresa.com; gestor@empresa.com"
                                    value={formParams.emailNotificacaoDestino || ''}
                                    onChange={e => setFormParams({...formParams, emailNotificacaoDestino: e.target.value})}
                                />
                                <small className="text-muted">Separe múltiplos e-mails com ponto e vírgula (;)</small>
                            </div>
                        </div>

                        <hr className="my-2 text-muted"/>
                        <h6 className="small fw-bold text-uppercase text-muted mb-3">Configuração do Servidor SMTP (Envio)</h6>

                        {/* Linha 2: Servidor e Porta */}
                        <div className="row mb-3">
                            <div className="col-md-6 form-group">
                                <label>Host SMTP</label>
                                <input 
                                    type="text" className="form-control" placeholder="ex: smtp.gmail.com"
                                    value={formParams.smtpHost || ''}
                                    onChange={e => setFormParams({...formParams, smtpHost: e.target.value})}
                                />
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Porta</label>
                                <input 
                                    type="number" className="form-control" placeholder="587"
                                    value={formParams.smtpPort || ''}
                                    onChange={e => setFormParams({...formParams, smtpPort: parseInt(e.target.value)})}
                                />
                            </div>
                            <div className="col-md-2 form-group pt-4">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" type="checkbox" id="checkAuth"
                                        checked={formParams.smtpAuth}
                                        onChange={e => setFormParams({...formParams, smtpAuth: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="checkAuth">Requer Autenticação</label>
                                </div>
                            </div>
                            <div className="col-md-2 form-group pt-4">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" type="checkbox" id="checkTls"
                                        checked={formParams.smtpStarttls}
                                        onChange={e => setFormParams({...formParams, smtpStarttls: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="checkTls">Usar STARTTLS</label>
                                </div>
                            </div>
                        </div>

                        {/* Linha 3: Credenciais */}
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>Usuário (E-mail Remetente)</label>
                                <input 
                                    type="text" className="form-control"
                                    value={formParams.smtpUsername || ''}
                                    onChange={e => setFormParams({...formParams, smtpUsername: e.target.value})}
                                />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>Senha (App Password)</label>
                                <input 
                                    type="password" className="form-control"
                                    value={formParams.smtpPassword || ''}
                                    onChange={e => setFormParams({...formParams, smtpPassword: e.target.value})}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. CONFIGURAÇÃO CNAB 240 */}
                <h6 className="text-muted border-bottom pb-2 mb-3 text-primary">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2"/> Contas Bancárias (CNAB 240)
                </h6>
                
                {listaContas.length > 0 && (
                    <div className="table-responsive mb-4">
                        <table className="table table-bordered table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Principal</th>
                                    <th>Nome da Conta</th>
                                    <th>Banco</th>
                                    <th>Ag/Conta</th>
                                    <th>Empresa</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listaContas.map((conta, idx) => (
                                    <tr key={idx} className={conta.principal ? "table-success" : ""}>
                                        <td className="text-center align-middle">
                                            {conta.principal ? (
                                                <span className="badge bg-success"><FontAwesomeIcon icon={faCheckCircle} /> Sim</span>
                                            ) : (
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => definirComoPrincipal(idx)}>
                                                    Definir
                                                </button>
                                            )}
                                        </td>
                                        <td className="align-middle fw-bold">{conta.nomeIdentificacao}</td>
                                        <td className="align-middle">{conta.bancoNome} ({conta.bancoCodigo})</td>
                                        <td className="align-middle">{conta.agencia}-{conta.agenciaDv} / {conta.conta}-{conta.contaDv}</td>
                                        <td className="align-middle">{conta.empresaNome}</td>
                                        <td className="text-center align-middle">
                                            <button type="button" className="btn btn-sm btn-primary me-2" onClick={() => editarConta(idx)} title="Editar">
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button type="button" className="btn btn-sm btn-danger" onClick={() => removerConta(idx)} title="Remover">
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* FORMULÁRIO CONTA BANCÁRIA (MANTIDO) */}
                <div className="card mb-4 border-light shadow-sm bg-light">
                    <div className="card-body">
                        <h6 className="small fw-bold text-uppercase text-secondary mb-3">
                            <FontAwesomeIcon icon={editandoIndex !== null ? faEdit : faPlus} /> {editandoIndex !== null ? 'Editar Conta' : 'Nova Conta Bancária'}
                        </h6>
                        
                        <div className="row mb-3">
                            <div className="col-md-4 form-group">
                                <label className="fw-bold">Nome de Identificação da Conta *</label>
                                <input 
                                    type="text" className="form-control" placeholder="Ex: Conta Principal Bradesco"
                                    value={contaForm.nomeIdentificacao}
                                    onChange={e => setContaForm({...contaForm, nomeIdentificacao: e.target.value})}
                                />
                            </div>
                            <div className="col-md-2 d-flex align-items-end mb-3 mb-md-0">
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" type="checkbox" id="checkPrincipal"
                                        checked={contaForm.principal}
                                        onChange={e => setContaForm({...contaForm, principal: e.target.checked})}
                                        disabled={listaContas.length === 0}
                                    />
                                    <label className="form-check-label" htmlFor="checkPrincipal">É a conta Principal?</label>
                                </div>
                            </div>
                        </div>

                        <hr className="my-2" />

                        <div className="row mb-2">
                            <div className="col-md-2 form-group">
                                <label>Cód. Banco</label>
                                <input type="text" className="form-control" value={contaForm.bancoCodigo} onChange={e => setContaForm({...contaForm, bancoCodigo: e.target.value})} maxLength={3}/>
                            </div>
                            <div className="col-md-4 form-group">
                                <label>Nome do Banco</label>
                                <input type="text" className="form-control" value={contaForm.bancoNome} onChange={e => setContaForm({...contaForm, bancoNome: e.target.value})} maxLength={30}/>
                            </div>
                            <div className="col-md-6 form-group">
                                <label>Código Convênio</label>
                                <input type="text" className="form-control" value={contaForm.convenio} onChange={e => setContaForm({...contaForm, convenio: e.target.value})} maxLength={20}/>
                            </div>
                        </div>
                        <div className="row mb-3">
                             <div className="col-md-2 form-group">
                                <label>Agência</label>
                                <input type="text" className="form-control" value={contaForm.agencia} onChange={e => setContaForm({...contaForm, agencia: e.target.value})} maxLength={5}/>
                            </div>
                            <div className="col-md-1 form-group">
                                <label>DV</label>
                                <input type="text" className="form-control" value={contaForm.agenciaDv} onChange={e => setContaForm({...contaForm, agenciaDv: e.target.value})} maxLength={1}/>
                            </div>
                            <div className="col-md-3 form-group">
                                <label>Conta Corrente</label>
                                <input type="text" className="form-control" value={contaForm.conta} onChange={e => setContaForm({...contaForm, conta: e.target.value})} maxLength={12}/>
                            </div>
                            <div className="col-md-1 form-group">
                                <label>DV</label>
                                <input type="text" className="form-control" value={contaForm.contaDv} onChange={e => setContaForm({...contaForm, contaDv: e.target.value})} maxLength={1}/>
                            </div>
                        </div>

                        <h6 className="small fw-bold text-uppercase text-secondary mt-3 mb-2"><FontAwesomeIcon icon={faBuilding} /> Dados da Empresa Pagadora (Nesta conta)</h6>
                        <div className="row mb-2">
                            <div className="col-md-6 form-group">
                                <label>Razão Social</label>
                                <input type="text" className="form-control" value={contaForm.empresaNome} onChange={e => setContaForm({...contaForm, empresaNome: e.target.value})} maxLength={30}/>
                            </div>
                            <div className="col-md-6 form-group">
                                <label>CNPJ</label>
                                <input type="text" className="form-control" value={contaForm.empresaCnpj} onChange={e => setContaForm({...contaForm, empresaCnpj: e.target.value.replace(/\D/g, '')})} maxLength={14}/>
                            </div>
                        </div>
                        <div className="row">
                             <div className="col-md-5 form-group">
                                <label>Logradouro</label>
                                <input type="text" className="form-control" value={contaForm.empresaLogradouro} onChange={e => setContaForm({...contaForm, empresaLogradouro: e.target.value})}/>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Número</label>
                                <input type="text" className="form-control" value={contaForm.empresaNumero} onChange={e => setContaForm({...contaForm, empresaNumero: e.target.value})}/>
                            </div>
                            <div className="col-md-3 form-group">
                                <label>Cidade</label>
                                <input type="text" className="form-control" value={contaForm.empresaCidade} onChange={e => setContaForm({...contaForm, empresaCidade: e.target.value})}/>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>UF</label>
                                <input type="text" className="form-control text-uppercase" value={contaForm.empresaUf} onChange={e => setContaForm({...contaForm, empresaUf: e.target.value})} maxLength={2}/>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                             {editandoIndex !== null && (
                                 <button type="button" className="btn btn-secondary me-2" onClick={limparFormularioConta}>
                                     Cancelar Edição
                                 </button>
                             )}
                             <button type="button" className="btn btn-success" onClick={salvarContaNaLista}>
                                 <FontAwesomeIcon icon={editandoIndex !== null ? faSave : faPlus} className="me-1"/> 
                                 {editandoIndex !== null ? 'Atualizar Conta na Lista' : 'Adicionar Conta'}
                             </button>
                        </div>
                    </div>
                </div>

                {/* 5. REGRAS DE MULTA */}
                <h6 className="text-muted border-bottom pb-2 mb-3 text-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2"/> Regras de Multa Liquidação
                </h6>
                <div className="row mb-4">
                    <div className="col-md-4 form-group">
                        <label>Multa após 30 Dias (%)</label>
                        <input type="number" step="0.01" className="form-control" value={formParams.percentualMulta30Dias} onChange={e => setFormParams({...formParams, percentualMulta30Dias: parseFloat(e.target.value)})}/>
                    </div>
                    <div className="col-md-4 form-group">
                        <label>Multa após 60 Dias (%)</label>
                        <input type="number" step="0.01" className="form-control" value={formParams.percentualMulta60Dias} onChange={e => setFormParams({...formParams, percentualMulta60Dias: parseFloat(e.target.value)})}/>
                    </div>
                    <div className="col-md-4 form-group">
                        <label>Multa após 90 Dias (%)</label>
                        <input type="number" step="0.01" className="form-control" value={formParams.percentualMulta90Dias} onChange={e => setFormParams({...formParams, percentualMulta90Dias: parseFloat(e.target.value)})}/>
                    </div>
                </div>

                {/* FOOTER */}
                {formParams.dataAtualizacao && (
                    <div className="row">
                        <div className="col-md-12 text-end text-muted small">
                            Última atualização: {format(new Date(formParams.dataAtualizacao), 'dd/MM/yyyy HH:mm')} 
                            {formParams.usuarioAtualizacao ? ` por ${formParams.usuarioAtualizacao}` : ''}
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end border-top pt-3 mt-2">
                    <button type="submit" className="btn btn-primary btn-lg">
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        Salvar Todas Configurações
                    </button>
                </div>

              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ComissaoParametrosGeraisComponent;